import LinkBankAccountIcon from '@assets/images/icons-outline/link-bank-icon.svg';
import type { Integration } from '@interfaces/integrations';
import { FeatureFlagTypes } from '@interfaces/organizationFeatureFlag';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { isFeatureEnabled } from '@utils/common';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { findIntegrationCondition } from '../findIntegrationCondition';

export type InputProp = {
  hasError: boolean;
  message: string;
  value: string;
};

const checkAutomaticBankAccountLinkingConfigurationValid = ({
  bankingIntegration,
  options,
}: {
  bankingIntegration?: Integration;
  options: any;
}) => {
  const autoBankAccountLinkingEnabled =
    options?.automatedBankAccountLinking ||
    isFeatureEnabled(FeatureFlagTypes.AUTOMATED_BANK_ACCOUNT_LINKING);

  if (bankingIntegration?.provider === 'FLINKS') {
    // FLINKS requires a iframe URL specified by the organization
    return (
      autoBankAccountLinkingEnabled &&
      bankingIntegration?.configuration?.clientIframeUrl
    );
  }

  return autoBankAccountLinkingEnabled;
};

const AddBankAccountInformation = ({
  onNext,
  onCancel,
  options,
}: {
  onNext: (index: number) => void;
  onCancel: () => void;
  options?: any;
}) => {
  const { integrations } = useContext(UserContext);
  const { sys } = useThemeTokens();
  const { t } = useTranslation(['components']);

  const bankingIntegration = integrations?.find(
    findIntegrationCondition(options),
  );

  const manualBankAccountLinkingEnabled: boolean =
    options?.manualbankAccountLinking ??
    isFeatureEnabled(FeatureFlagTypes.MANUALLY_ADD_BANK_ACCOUNT);

  const autoBankAccountLinkingEnabled =
    checkAutomaticBankAccountLinkingConfigurationValid({
      bankingIntegration,
      options,
    });

  return (
    <Form>
      <DialogTitle
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Typography variant='headingLarge'>
          {t('components:bankAccountSelect.information.header')}
        </Typography>
        <IconButton onClick={() => onCancel()}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img
          src={LinkBankAccountIcon}
          alt='link-bank-account'
          style={{ marginBottom: sys.spacing.xxl }}
        />
        <Typography
          textAlign='center'
          style={{ marginBottom: sys.spacing.xxl }}
          variant='titleLarge'
        >
          {t('components:bankAccountSelect.information.title')}
        </Typography>
        <Typography
          style={{ marginBottom: sys.spacing.xxl }}
          variant='bodyMedium'
          textAlign='center'
        >
          {t('components:bankAccountSelect.information.body')}
        </Typography>
        {autoBankAccountLinkingEnabled && (
          <Button
            onClick={() => onNext(2)}
            sx={{ marginBottom: sys.spacing.md }}
            label={t('components:bankAccountSelect.information.addAccountBtn')}
          />
        )}
        {manualBankAccountLinkingEnabled ? (
          <Button
            onClick={() => onNext(3)}
            variant='text'
            label={t('components:bankAccountSelect.information.addManuallyBtn')}
            sx={{ fontSize: '14px', fontWeight: '500' }}
          />
        ) : undefined}
      </DialogContent>
    </Form>
  );
};

export default AddBankAccountInformation;
